import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { CopyableCell } from '../../components/ui/CopyableCell';
import { CopyableLinkCell } from '../../components/ui/CopyableLinkCell';
import { useCurrentUser } from '../auth';
import { listCampaigns } from '../campaigns/api';
import { centsToDisplay } from '../../lib/money';
import { isBudgetModified } from '../../lib/dailyBudget';
import { resolveRowGoogleAdLabel } from '../../lib/resolveRowTemplates';
import {
  hasEndDateDrift,
  hasRowModifications,
  hasStartDateDrift,
} from './hasRowModifications';
import {
  buildRowRenderContext,
  renderRowHeadlineArray,
  renderRowTemplate,
  renderRowTemplateArray,
  slugifyPath,
} from '../../lib/rowContent';
import { resolveRowLandingUrl } from '../../lib/landingPageResolution';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { resolveGroupTag } from '../../lib/groupTag';
import { ApiError } from '../../lib/api';
import type {
  AccountCampaign,
  AccountCampaignBulkAction,
  AccountCampaignBulkResult,
  AccountCampaignInlinePatch,
} from '../../types/accountCampaign';
import type { Campaign, GoogleAssets, UtmConfig } from '../../types/campaign';
import type { Rollout } from '../../types/rollout';
import { bulkAction, getRolloutRows, listUsers, patchRow, refreshFields } from './api';
import { bulkActionLabel } from './bulkActionLabels';
import { BulkRefreshFromCampaignModal } from './BulkRefreshFromCampaignModal';
import { BulkRejectionToast } from './BulkRejectionToast';
import { DailyBudgetCell } from './DailyBudgetCell';
import { GridBulkExecutionBar } from './GridBulkExecutionBar';
import { NotesCell } from './NotesCell';
import { ReadOnlyArrayCell } from './ReadOnlyArrayCell';
import { RolloutTabStrip } from './RolloutTabStrip';
import { computeGridCounts } from './rolloutGridCounts';
import { BuilderCell } from './BuilderCell';
import { BuildViewLink } from './BuildViewLink';
import { GroupBadge } from './GroupBadge';
import { GridFilterBar } from './GridFilterBar';
import { GoogleExportButtons } from './GoogleExportButtons';
import { GoogleErrorsModal } from './GoogleErrorsModal';
import {
  validateGoogleContent,
  type GoogleValidationResult,
  type RowWithDeps,
} from '../../lib/validateGoogleContent';
import {
  DEFAULT_GRID_FILTERS,
  readGridFiltersFromParams,
  writeGridFiltersToParams,
  type GridFilters,
} from '../../lib/gridFilterUrl';
import { applyGridFilters, deriveGridOptions } from '../../lib/applyGridFilters';
import {
  compareRows,
  DEFAULT_SORT,
  type SortLookups,
  type SortState,
} from './gridSort';
import { SortableHeaderButton } from './SortableHeaderButton';

// P2b-3 §4: Google execution grid. Mirrors MetaGrid's shape with
// Google-specific columns (no ad-type / body-copy / CTA / lead-form;
// path1+path2 + headlines/descriptions; Scheduled instead of Live;
// no Local Built). The shared spine — sticky tab strip, server-side
// gridFilter='google', selection + bulk bar, NotesCell on the Notes
// column, DailyBudgetCell — comes from the same components MetaGrid uses.

export function GoogleGrid() {
  const { id } = useParams<{ id: string }>();
  const currentUser = useCurrentUser();
  const qc = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRefreshTarget, setBulkRefreshTarget] = useState<{
    campaign: Campaign;
    selectedRows: AccountCampaign[];
  } | null>(null);
  const [bulkResult, setBulkResult] = useState<
    null | { label: string; result: AccountCampaignBulkResult }
  >(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(
    () => readGridFiltersFromParams(searchParams, 'google'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [filters, setFilters] = useState<GridFilters>(initialFilters);
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    writeGridFiltersToParams(next, filters);
    if (next.toString() === searchParams.toString()) return;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);

  const googleRowsQuery = useQuery({
    queryKey: ['rollout', id, 'rows', 'google'],
    queryFn: () => getRolloutRows(id!, { gridFilter: 'google' }),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });
  const masterRowsQuery = useQuery({
    queryKey: ['rollout', id, 'rows'],
    queryFn: () => getRolloutRows(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });

  const brandAccountId = googleRowsQuery.data?.rollout.brandAccountId;
  const campaignsQuery = useQuery({
    queryKey: ['campaigns', 'brand', brandAccountId],
    queryFn: () => listCampaigns({ brandAccountId: brandAccountId! }),
    enabled: !!brandAccountId,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => listUsers(),
  });

  const allCampaigns = campaignsQuery.data ?? [];
  const allUsers = usersQuery.data ?? [];

  const accountsById = useMemo(() => {
    const accs = googleRowsQuery.data?.accounts ?? [];
    return new Map(accs.map((a) => [a.id, a]));
  }, [googleRowsQuery.data]);
  const campaignsById = useMemo(
    () => new Map(allCampaigns.map((c) => [c.id, c])),
    [allCampaigns],
  );
  const usersById = useMemo(() => new Map(allUsers.map((u) => [u.uid, u])), [allUsers]);
  const profileById = useMemo(() => {
    const profiles = googleRowsQuery.data?.locationProfiles ?? [];
    return new Map(profiles.map((p) => [p.accountId, p]));
  }, [googleRowsQuery.data]);

  const counts = useMemo(() => {
    const masterRows = masterRowsQuery.data?.rows ?? [];
    const masterAccounts = masterRowsQuery.data?.accounts ?? [];
    const masterProfiles = masterRowsQuery.data?.locationProfiles ?? [];
    const archived = masterRowsQuery.data?.rollout.status === 'archived';
    return computeGridCounts({
      rows: masterRows,
      accountsById: new Map(masterAccounts.map((a) => [a.id, a])),
      campaignsById: new Map(allCampaigns.map((c) => [c.id, c])),
      locationProfilesById: new Map(masterProfiles.map((p) => [p.accountId, p])),
      rolloutArchived: !!archived,
    });
  }, [masterRowsQuery.data, allCampaigns]);

  const patchMut = useMutation({
    mutationFn: ({ rowId, patch }: { rowId: string; patch: AccountCampaignInlinePatch }) =>
      patchRow(id!, rowId, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['rollout', id, 'rows', 'google'] });
    },
  });
  const patchField = (rowId: string, patch: AccountCampaignInlinePatch) => {
    patchMut.mutate({ rowId, patch });
  };
  const patchFieldAsync = async (rowId: string, patch: AccountCampaignInlinePatch) => {
    await patchMut.mutateAsync({ rowId, patch });
  };

  const bulkMut = useMutation({
    mutationFn: async (action: AccountCampaignBulkAction) => bulkAction(id!, action),
  });
  const dispatchBulk = async (action: AccountCampaignBulkAction) => {
    try {
      const result = await bulkMut.mutateAsync(action);
      setBulkResult({ label: bulkActionLabel(action), result });
      await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
      await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows', 'google'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      setBulkResult({
        label: bulkActionLabel(action),
        result: {
          succeeded: 0,
          rejected: [{ accountCampaignId: '(none)', locationName: '(all rows)', reasons: [msg] }],
        },
      });
    }
  };

  const toggleSelect = (rowId: string, next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      if (next) copy.add(rowId);
      else copy.delete(rowId);
      return copy;
    });
  };

  // Errors modal state. Two entry points: the "Check Errors" button
  // (defaults to filtered scope — matches the grid's visual context)
  // and the export modal's "View Errors" action (carries the export's
  // scope so the user sees what's gating the export).
  // MUST live above the early returns below so the hook count is
  // stable across loading → loaded transitions (Rules of Hooks).
  const [errorsModalScope, setErrorsModalScope] =
    useState<null | 'filtered' | 'all'>(null);

  // ---- Data extraction with safe defaults (Rules of Hooks) -------
  // Pull the bits the validation memos depend on out of the query
  // payload BEFORE the early returns, with `?? null` / `?? []`
  // fallbacks, so the hooks below run on every render. The memo
  // callbacks bail early when rollout is null (loading state) and
  // return an empty validation result.
  const queryData = googleRowsQuery.data;
  const safeRollout: Rollout | null = queryData?.rollout ?? null;
  const safeGateRows = queryData?.rows ?? [];
  const safeBrandFranchiseProfile = queryData?.brandFranchiseProfile ?? null;

  // Filter + sort derivation. Pure functions (applyGridFilters /
  // compareRows). Lifted above the early returns so the validation
  // memos can take `rows` as a dep — keeping the post-filter
  // post-sort projection a single source of truth for both the
  // memos and the JSX render path below.
  const filteredRows = applyGridFilters({
    rows: safeGateRows,
    filters,
    accountsById,
    campaignsById,
    usersById,
    platform: 'google',
    currentUserUid: currentUser.uid,
  });
  const sortLookups: SortLookups = { accountsById, campaignsById, usersById };
  const rows = sort
    ? [...filteredRows].sort((a, b) => compareRows(a, b, sort, sortLookups))
    : filteredRows;

  // Pre-export validation. Pre-compute once per scope so both the
  // grid's "Check Errors" button and the export modal's pre-export
  // gate can read directly without re-running. The validator is pure
  // and cheap (~ms for 500 rows), but we still memo to avoid the
  // re-run on every grid re-render.
  //
  // Both useMemo calls MUST run on every render, so they live above
  // the early returns. While loading, `safeRollout` is null and the
  // zipper bails — both memos resolve to an empty result.
  const zipRowsWithDeps = (input: AccountCampaign[]): RowWithDeps[] => {
    if (!safeRollout) return [];
    const out: RowWithDeps[] = [];
    for (const r of input) {
      const account = accountsById.get(r.accountId);
      if (!account) continue;
      const campaign = r.campaignId ? campaignsById.get(r.campaignId) ?? null : null;
      const locationProfile = profileById.get(r.accountId) ?? null;
      out.push({
        row: r,
        account,
        locationProfile,
        brandProfile: safeBrandFranchiseProfile,
        campaign,
        rollout: safeRollout,
      });
    }
    return out;
  };
  const validationFiltered: GoogleValidationResult = useMemo(
    () => validateGoogleContent(zipRowsWithDeps(rows)),
    // rows is the post-filter post-sort projection; deps inputs are
    // stable references for a given query payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      rows,
      accountsById,
      campaignsById,
      profileById,
      safeBrandFranchiseProfile,
      safeRollout,
    ],
  );
  const validationGate: GoogleValidationResult = useMemo(
    () => validateGoogleContent(zipRowsWithDeps(safeGateRows)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      safeGateRows,
      accountsById,
      campaignsById,
      profileById,
      safeBrandFranchiseProfile,
      safeRollout,
    ],
  );

  // ---- Early returns ----------------------------------------------
  if (
    googleRowsQuery.isLoading ||
    masterRowsQuery.isLoading ||
    campaignsQuery.isLoading ||
    usersQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  if (googleRowsQuery.error || !googleRowsQuery.data) {
    return (
      <div className="text-red-600 text-sm">
        Failed to load Google grid:{' '}
        {(googleRowsQuery.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  // Past this point the query data is loaded; the safe* vars above
  // are non-null/non-empty in practice. Re-narrow into the names the
  // JSX uses so we don't sprinkle non-null assertions through the
  // render path.
  const { rollout, rows: gateRows, brandFranchiseProfile } = googleRowsQuery.data;
  const brandAccount = accountsById.get(rollout.brandAccountId);
  const archived = rollout.status === 'archived';

  const filterOptions = deriveGridOptions({
    rows: gateRows,
    accountsById,
    campaignsById,
    usersById,
    platform: 'google',
  });

  const errorsResult =
    errorsModalScope === 'all' ? validationGate : validationFiltered;

  // Header column tally:
  //   Context: 13 | Budget: 4 | Execution: 5
  //   Selection col: 1 (hidden when archived)
  // Round-1 polish Fix 6: +1 Context column for the per-row Google builder.
  // Round-2 polish Fix 3: +1 Context column for Local Assets (sits
  // between Asset folder and Google ad account).
  const contextCols = 14;
  const budgetCols = 4;
  const executionCols = 5;
  const totalCols = (archived ? 0 : 1) + contextCols + budgetCols + executionCols;

  const allSelected =
    !archived && rows.length > 0 && rows.every((r) => selectedIds.has(r.id));
  const toggleAll = (next: boolean) => {
    setSelectedIds((prev) => {
      const copy = new Set(prev);
      for (const r of rows) {
        if (next) copy.add(r.id);
        else copy.delete(r.id);
      }
      return copy;
    });
  };

  return (
    <div className="space-y-4">
      <RolloutTabStrip
        rolloutId={rollout.id}
        active="google"
        metaCount={counts.meta}
        googleCount={counts.google}
      />

      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-500">
            <Link to="/rollouts" className="hover:underline">Rollouts</Link>
            <span className="mx-1 text-slate-300">/</span>
            {brandAccount ? (
              <Link to={`/accounts/${brandAccount.id}`} className="hover:underline">
                {brandAccount.name}
              </Link>
            ) : null}
            <span className="mx-1 text-slate-300">/</span>
            <Link to={`/rollouts/${rollout.id}`} className="hover:underline">
              {rollout.name}
            </Link>
          </p>
          <h1 className="text-2xl font-semibold text-slate-900">Google build</h1>
          <p className="text-sm text-slate-500 mt-1">
            {gateRows.length === rows.length
              ? `${rows.length} ${rows.length === 1 ? 'row' : 'rows'} ready to build`
              : `${rows.length} of ${gateRows.length} ${gateRows.length === 1 ? 'row' : 'rows'} shown`}
            {rollout.monthYearLabel ? ` · ${rollout.monthYearLabel}` : ''}
            {archived && (
              <span className="ml-2 text-xs uppercase tracking-wide text-slate-400">
                read-only (archived)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setErrorsModalScope('filtered')}
            disabled={googleRowsQuery.isLoading || masterRowsQuery.isLoading}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title={
              validationFiltered.violations.length === 0
                ? 'No validation errors'
                : `${validationFiltered.violations.length} validation ${
                    validationFiltered.violations.length === 1 ? 'error' : 'errors'
                  } across ${validationFiltered.locationCount} ${
                    validationFiltered.locationCount === 1 ? 'location' : 'locations'
                  }`
            }
          >
            Check Errors
            {validationFiltered.violations.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                {validationFiltered.violations.length}
              </span>
            )}
          </button>
          <GoogleExportButtons
            rolloutId={rollout.id}
            filters={filters}
            filteredCount={rows.length}
            gateCount={gateRows.length}
            archived={archived}
            loading={googleRowsQuery.isLoading || masterRowsQuery.isLoading}
            validationFiltered={validationFiltered}
            validationGate={validationGate}
            onShowErrors={(scope) => setErrorsModalScope(scope)}
          />
        </div>
      </div>

      {errorsModalScope !== null && (
        <GoogleErrorsModal
          rolloutId={rollout.id}
          result={errorsResult}
          filteredRowIds={rows.map((r) => r.id)}
          onClose={() => setErrorsModalScope(null)}
        />
      )}

      <GridFilterBar
        filters={filters}
        onChange={setFilters}
        amsInGrid={filterOptions.ams}
        campaignsInGrid={filterOptions.campaigns}
        groupsInGrid={filterOptions.groups}
        hasNoGroup={filterOptions.hasNoGroup}
        buildersInGrid={filterOptions.builders}
        hasUnassignedBuilder={filterOptions.hasUnassignedBuilder}
        platform="google"
      />

      <GridBulkExecutionBar
        platform="google"
        selectedIds={[...selectedIds]}
        onClear={() => setSelectedIds(new Set())}
        onDispatch={dispatchBulk}
        archived={archived}
        users={allUsers}
        rows={rows}
        campaigns={allCampaigns}
        onRefreshFromCampaign={(c) =>
          setBulkRefreshTarget({
            campaign: c,
            selectedRows: rows.filter((r) => selectedIds.has(r.id)),
          })
        }
      />

      {bulkRefreshTarget && (
        <BulkRefreshFromCampaignModal
          campaign={bulkRefreshTarget.campaign}
          selectedRows={bulkRefreshTarget.selectedRows}
          onSubmit={async (fieldKeys) => {
            await refreshFields(
              id!,
              bulkRefreshTarget.selectedRows.map((r) => r.id),
              fieldKeys,
            );
            await qc.invalidateQueries({ queryKey: ['rollout', id, 'rows'] });
            await qc.invalidateQueries({
              queryKey: ['rollout', id, 'rows', 'google'],
            });
          }}
          onClose={() => setBulkRefreshTarget(null)}
        />
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="min-w-max text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {!archived && (
                <th rowSpan={2} className="px-2 py-2 bg-slate-50 border-b border-slate-200">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleAll(e.target.checked)}
                    aria-label="Select all visible rows"
                  />
                </th>
              )}
              <th
                colSpan={contextCols}
                className="px-2 py-2 font-semibold text-center bg-slate-50 border-b border-slate-200"
              >
                Context
              </th>
              <th
                colSpan={budgetCols}
                className="px-2 py-2 font-semibold text-center bg-slate-100 border-b border-slate-200"
              >
                Budget
              </th>
              <th
                colSpan={executionCols}
                className="px-2 py-2 font-semibold text-center bg-slate-50 border-b border-slate-200"
              >
                Execution
              </th>
            </tr>
            <tr>
              {/* Context columns */}
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 sticky left-0 bg-slate-50 z-10">
                <SortableHeaderButton column="location" label="Location" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="am" label="AM" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                Builder
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="campaign" label="Campaign" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                Resolved Google ad label
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Headlines</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Descriptions</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Path1</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Path2</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Landing URL</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">UTM</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Asset folder</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Local assets</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Google ad account</th>
              {/* Budget columns */}
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">Monthly</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">Increase</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">Daily</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-100">
                <SortableHeaderButton column="startDate" label="Dates" sort={sort} onChange={setSort} />
              </th>
              {/* Execution columns */}
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="googleBuilt" label="Main Built" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">
                <SortableHeaderButton column="googleScheduled" label="Scheduled" sort={sort} onChange={setSort} />
              </th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Budget Updated</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Preview Links</th>
              <th className="px-2 py-2 text-left font-medium border-b border-slate-200 bg-slate-50">Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={totalCols}
                  className="px-4 py-6 text-center text-slate-500 text-sm"
                >
                  {gateRows.length === 0 ? (
                    'No rows ready for Google build. AMs need to mark rows ready and set budgets.'
                  ) : (
                    <>
                      No rows match these filters.{' '}
                      <button
                        type="button"
                        onClick={() => setFilters(DEFAULT_GRID_FILTERS)}
                        className="text-blue-600 hover:underline"
                      >
                        Clear filters
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const acc = accountsById.get(row.accountId);
                if (!acc) return null;
                const campaign = row.campaignId ? campaignsById.get(row.campaignId) ?? null : null;
                const profile = profileById.get(acc.id) ?? null;
                const am = row.assignedAmId ? usersById.get(row.assignedAmId) ?? null : null;
                const assets: GoogleAssets | null = row.googleAssets;
                const utm: UtmConfig | null = row.utmConfig;
                const resolvedLabel = resolveRowGoogleAdLabel({
                  row: {
                    campaignDescriptor: row.campaignDescriptor,
                    metaCampaignNameTemplate: row.metaCampaignNameTemplate,
                    googleAdLabelTemplate: row.googleAdLabelTemplate,
                  },
                  campaign,
                  account: acc,
                  locationProfile: profile,
                  brandProfile: brandFranchiseProfile,
                  rollout,
                });
                const assetFolder = row.googleAssetsFolderUrl ?? campaign?.googleAssetsFolderUrl ?? null;
                const dateRange = formatDateRange(row.startDate, row.endDate);
                const locationLabel = profile?.franchiseSuburb?.trim() || acc.name;
                // Group pill mirrors the Master grid (parent-chain walk).
                const group = resolveGroupTag(acc.id, accountsById);
                // P2b-3 round-1 polish Fix 3: substitute placeholders in
                // googleAssets content (headlines, descriptions, paths)
                // before display.
                const renderCtx = buildRowRenderContext({
                  row,
                  campaign,
                  account: acc,
                  locationProfile: profile,
                  brandProfile: brandFranchiseProfile,
                  rollout,
                });
                // Pinned Headlines spec §2.3: render text + pin
                // together; the popover renders P1/P2/P3 badges via
                // ReadOnlyArrayCell's `pins` prop.
                const headlineEntries = renderRowHeadlineArray(
                  assets?.headlines ?? null,
                  renderCtx,
                );
                const headlinesRendered = headlineEntries.map((h) => h.text);
                const headlinePins = headlineEntries.map((h) => h.pinPosition);
                const descriptionsRendered = renderRowTemplateArray(
                  assets?.descriptions ?? null,
                  renderCtx,
                );
                const path1Rendered = renderRowTemplate(assets?.path1 ?? null, renderCtx);
                // Google Ads paths reject spaces — slugify path2 after
                // template substitution so values like "Botanic Ridge"
                // (from {shortSuburb}) become "Botanic-Ridge".
                const path2Rendered = slugifyPath(
                  renderRowTemplate(assets?.path2 ?? null, renderCtx),
                );
                // Round-2 polish Fix 4: same modification signals as
                // the Meta grid + master grid. Helpers handle the
                // opt-out / unassigned / legacy-undefined edge cases
                // internally.
                const showContentDot = hasRowModifications(row, campaign);
                const startDrift = hasStartDateDrift(row);
                const endDrift = hasEndDateDrift(row);
                const showDateDot = startDrift || endDrift;
                const dateDotTitle =
                  startDrift && endDrift
                    ? 'Both dates modified'
                    : startDrift
                      ? 'Start date modified'
                      : 'End date modified';
                // Round-1 polish follow-up: resolved landing URL still
                // carries brand-template placeholders ({slug},
                // {suburbSlug}, etc.) — pipe through renderRowTemplate
                // so the cell shows the per-location URL.
                const landingUrlRaw = resolveRowLandingUrl(
                  row,
                  brandFranchiseProfile,
                  profile,
                  'google',
                );
                const landingUrl = renderRowTemplate(landingUrlRaw, renderCtx) || null;
                return (
                  <tr
                    key={row.id}
                    className={`border-t border-slate-100 hover:bg-slate-50 ${
                      !row.primary ? 'bg-slate-50/40' : ''
                    }`}
                  >
                    {!archived && (
                      <td className="px-2 py-1.5 align-top">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.id)}
                          onChange={(e) => toggleSelect(row.id, e.target.checked)}
                          aria-label={`Select ${acc.name}`}
                        />
                      </td>
                    )}
                    <td className="px-2 py-1.5 align-top sticky left-0 bg-white">
                      <div className="font-medium inline-flex items-center gap-1.5">
                        <BuildViewLink
                          rolloutId={rollout.id}
                          rowId={row.id}
                          platform="google"
                          locationLabel={locationLabel}
                          filteredRowIds={rows.map((r) => r.id)}
                        />
                        <Link to={`/accounts/${acc.id}`} className="hover:underline" title={acc.name}>
                          {locationLabel}
                        </Link>
                        <GroupBadge name={group?.name ?? null} />
                        {showContentDot && (
                          <span
                            aria-hidden="true"
                            title="Row content modified from Campaign"
                            className="inline-block w-2 h-2 rounded-full bg-orange-500"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 align-top text-slate-700 whitespace-nowrap">
                      {am ? formatUserDisplayName(am) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      <BuilderCell
                        value={row.googleBuilderId}
                        users={allUsers}
                        disabled={archived}
                        ariaLabel={`Google builder for ${acc.name}`}
                        onChange={(next) => patchField(row.id, { googleBuilderId: next })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {row.campaignNameOverride ?? campaign?.name ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {resolvedLabel.resolved ? (
                        <CopyableCell
                          value={resolvedLabel.resolved}
                          ariaLabel={`Resolved Google ad label for ${acc.name}`}
                          display={
                            <span className="font-mono text-xs">{resolvedLabel.resolved}</span>
                          }
                        />
                      ) : (
                        <span className="text-slate-400" title="No ad-label template set">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <ReadOnlyArrayCell
                        values={headlinesRendered}
                        pins={headlinePins}
                        mode="first-three-list"
                        ariaLabel={`Headlines for ${acc.name}`}
                        popoverTitle={`Headlines — ${acc.name}`}
                        className="w-48"
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <ReadOnlyArrayCell
                        values={descriptionsRendered}
                        mode="first-three-list"
                        ariaLabel={`Descriptions for ${acc.name}`}
                        popoverTitle={`Descriptions — ${acc.name}`}
                        className="w-48"
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap text-xs">
                      {path1Rendered.length > 0 ? path1Rendered : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap text-xs">
                      {path2Rendered.length > 0 ? path2Rendered : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CopyableLinkCell
                        href={landingUrl}
                        ariaLabel={`Landing URL for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {renderUtmSummary(utm, 'google')}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CopyableLinkCell
                        href={assetFolder}
                        display={<span>Open</span>}
                        ariaLabel={`Google asset folder for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {/* Round-2 polish Fix 3: Local Assets URL,
                          read-only here (edits via master grid /
                          row editor). */}
                      <CopyableLinkCell
                        href={row.localAssetsUrl}
                        display={<span>Open</span>}
                        ariaLabel={`Local assets for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <CopyableLinkCell
                        href={acc.googleAdsUrl}
                        display={<span>Open</span>}
                        ariaLabel={`Google ad account for ${acc.name}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {centsToDisplay(row.googleMonthlyBudget, acc.currency)}
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap">
                      {row.googleCampaignIncrease > 0
                        ? centsToDisplay(row.googleCampaignIncrease, acc.currency)
                        : '—'}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <DailyBudgetCell
                        row={row}
                        platform="google"
                        currency={acc.currency}
                        modified={isBudgetModified(row, 'google')}
                        accountMonthlyBudget={acc.googleMasterMonthlyBudget}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top whitespace-nowrap text-xs text-slate-700">
                      <span className="inline-flex items-center gap-1.5">
                        {dateRange}
                        {showDateDot && (
                          <span
                            aria-hidden="true"
                            title={dateDotTitle}
                            className="inline-block w-2 h-2 rounded-full bg-orange-500"
                          />
                        )}
                      </span>
                    </td>
                    {/* Execution */}
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={row.googleBuilt}
                        disabled={archived}
                        onChange={(e) => patchField(row.id, { googleBuilt: e.target.checked })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={row.googleScheduled}
                        disabled={archived}
                        onChange={(e) => patchField(row.id, { googleScheduled: e.target.checked })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="checkbox"
                        checked={row.googlePostRolloutCheck}
                        disabled={archived}
                        onChange={(e) =>
                          patchField(row.id, { googlePostRolloutCheck: e.target.checked })
                        }
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <PreviewLinksInput
                        value={row.googlePreviewLinks}
                        archived={archived}
                        ariaLabel={`Google preview links for ${acc.name}`}
                        onCommit={(next) => patchField(row.id, { googlePreviewLinks: next })}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <NotesCell
                        value={row.rowNotes}
                        locationLabel={locationLabel}
                        archived={archived}
                        onSave={(next) => patchFieldAsync(row.id, { rowNotes: next })}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {bulkResult && (
        <BulkRejectionToast
          actionLabel={bulkResult.label}
          result={bulkResult.result}
          onDismiss={() => setBulkResult(null)}
        />
      )}
    </div>
  );
}

// ---------- helpers ----------

function formatDateRange(startIso: string | null, endIso: string | null): string {
  const fmt = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (!a && !b) return '—';
  if (a && b) return `${a} → ${b}`;
  return a ?? b ?? '—';
}

function renderUtmSummary(utm: UtmConfig | null, platform: 'meta' | 'google') {
  if (!utm || !utm.enabled) return <span className="text-slate-400 text-xs">disabled</span>;
  if (!utm.platforms.includes(platform)) {
    return <span className="text-slate-400 text-xs">off ({platform})</span>;
  }
  const campaignName = utm.shared.campaignName ?? null;
  const platformConfig = platform === 'meta' ? utm.meta : utm.google;
  const tooltip = platformConfig
    ? `source=${platformConfig.source} · medium=${platformConfig.medium} · term=${platformConfig.term} · content=${platformConfig.content}`
    : undefined;
  return (
    <span className="text-xs text-slate-700" title={tooltip}>
      on{campaignName ? ` · ${campaignName}` : ''}
    </span>
  );
}

function PreviewLinksInput({
  value,
  archived,
  ariaLabel,
  onCommit,
}: {
  value: string | null;
  archived: boolean;
  ariaLabel: string;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState<string>(value ?? '');
  return (
    <input
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const trimmed = draft.trim();
        const next = trimmed.length === 0 ? null : trimmed;
        if (next !== (value ?? null)) onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(value ?? '');
          (e.target as HTMLInputElement).blur();
        }
      }}
      readOnly={archived}
      placeholder={archived ? '' : 'Paste preview URL'}
      aria-label={ariaLabel}
      className="w-44 text-xs rounded border border-slate-200 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-400"
    />
  );
}
